import React, { useEffect, useRef } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { CryptoFaqContent } from './dashboard/CryptoFaqSection';

type Props = {
    open: boolean;
    onClose: () => void;
};

export const CryptoFaqDialog: React.FC<Props> = ({ open, onClose }) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="crypto-faq-title"
                className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
            >
                <header className="flex items-start justify-between gap-4 border-b-4 border-b-primary bg-brand-ink px-5 py-4">
                    <div className="flex items-start gap-3">
                        <span className="rounded-md bg-primary/15 p-2">
                            <HelpCircle className="h-5 w-5 text-primary" aria-hidden />
                        </span>
                        <div>
                            <h2 id="crypto-faq-title" className="font-semibold text-white">
                                Crypto FAQ
                            </h2>
                            <p className="mt-0.5 text-xs text-white/70">
                                Search common crypto investigation terms without leaving your current page.
                            </p>
                        </div>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        aria-label="Close Crypto FAQ"
                        className="rounded-md p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="p-5">
                    <CryptoFaqContent listClassName="max-h-[55vh]" />
                </div>
            </section>
        </div>
    );
};
